/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { createSelector } from '@reduxjs/toolkit';
import { produce } from 'immer';
import {
  SYNCGROUPS_TYPE_SETBBOX,
  SYNCGROUPS_TYPE_SETFEATURES,
  SYNCGROUPS_TYPE_SETTIME,
} from './constants';
import {
  getTakenWarningTimelineIds,
  isWarningsTimelineLinkSourceAllowed,
} from '../linking';
import { getFeatureLinksForAllPanelsFromState } from './featureLinks';
import { constructSyncSourceDisplayName } from './syncSourceDisplayName';
import type {
  SyncGroupViewState,
  SynchronizationGroups,
  SynchronizationSources,
  SyncType,
} from './types';
import type { CoreAppStore } from '../../types';

const emptyState: SyncGroupViewState = {
  timeslider: {
    groups: [],
    sourcesById: [],
  },
  zoompane: {
    groups: [],
    sourcesById: [],
  },
  level: {
    groups: [],
    sourcesById: [],
  },
  features: {
    groups: [],
    sourcesById: [],
  },
};

export const groupTypes = [
  {
    title: 'Timeslider',
    syncType: SYNCGROUPS_TYPE_SETTIME as SyncType,
    groupType: 'timeslider',
  },
  {
    title: 'Zoompane',
    syncType: SYNCGROUPS_TYPE_SETBBOX as SyncType,
    groupType: 'zoompane',
  },
  {
    title: 'Features',
    syncType: SYNCGROUPS_TYPE_SETFEATURES as SyncType,
    groupType: 'features',
  },
];

/*
    _____ Creates viewState  _____
      - Runs Each time viewState is created:
      - Fill arrays inside initialState with objects containing new values from Redux
*/
export const createSyncGroupViewState = (
  groups: SynchronizationGroups,
  sources: SynchronizationSources,
  viewsById?: Record<
    string,
    { id?: string; title?: string; componentType?: string }
  >,
  linksByPanelId?: Record<string, string[]>,
): SyncGroupViewState => {
  const initialState = produce(emptyState, (draft) => {
    /*
      _____ 1. Fill groups _____
        1. Goes through all groups by ID
        2. Collects original timeslider or zoompane values into an array
        3. Sorts and adds selected sources to ViewState based on ID type
    */
    Object.keys(groups.byId).forEach((id) => {
      const synchronizationGroup = groups.byId[id];
      const groupType = groupTypes.find(
        (object) => object.syncType === synchronizationGroup.type,
      )?.groupType;
      if (groupType) {
        const sourcePanelId =
          groupType === 'features'
            ? synchronizationGroup.sourcePanelId
            : undefined;
        const name =
          groupType === 'features' && sourcePanelId
            ? constructSyncSourceDisplayName(
                sourcePanelId!,
                viewsById?.[sourcePanelId!],
                sourcePanelId!,
              )
            : undefined;
        draft[groupType].groups.push({
          id,
          name,
          selected:
            groupType === 'features' && sourcePanelId
              ? linksByPanelId?.[sourcePanelId] || []
              : synchronizationGroup.targets.allIds,
        });
      }
    });
    /*
      _____ 2. Fill sourcesById _____
        1. Loop though sources and add source to each group type
    */
    Object.keys(sources.byId).forEach((id) => {
      groupTypes.forEach(({ syncType, groupType }) => {
        // Feature sync groups show selectable map sources only.
        if (groupType === 'features') {
          if (
            sources.byId[id].types.includes(SYNCGROUPS_TYPE_SETBBOX) &&
            sources.byId[id].types.includes(SYNCGROUPS_TYPE_SETTIME)
          ) {
            draft[groupType].sourcesById.push({
              id,
              name: constructSyncSourceDisplayName(id, viewsById?.[id], id),
            });
          }
          return;
        }

        if (sources.byId[id].types.includes(syncType)) {
          draft[groupType].sourcesById.push({
            id,
            name: constructSyncSourceDisplayName(id, viewsById?.[id], id),
          });
        }
      });
    });

    /*
      _____ 3. Compute warning timeline availability _____
        1. Collect timeline ids already linked by warning list panels
        2. Use this to mark timelines as unavailable for other warning lists
    */
    const takenWarningTimelineIds = getTakenWarningTimelineIds(
      linksByPanelId || {},
      viewsById || {},
    );

    /*
      _____ 4. Build feature-group -> source-panel mapping _____
        1. For each Features_<panelId> group, store which source panel it belongs to
        2. Needed to apply component-type compatibility rules per feature group
    */
    const featureGroupIdsBySourceId: Record<string, string[]> = {};
    draft.features.groups.forEach((group) => {
      const sourcePanelId = groups.byId[group.id]?.sourcePanelId;
      if (!sourcePanelId) {
        return;
      }
      featureGroupIdsBySourceId[sourcePanelId] = [
        ...(featureGroupIdsBySourceId[sourcePanelId] || []),
        group.id,
      ];
    });

    /*
      _____ 5. Add warning timeline targets to feature options _____
        1. Warning timeline can be listed as a selectable target in Features groups
        2. It can be disabled globally (already taken) or per group (incompatible source type)
        3. If a group becomes incompatible, remove timeline from that group's selected ids
           so UI state immediately reflects the new compatibility
    */
    Object.keys(viewsById || {}).forEach((id) => {
      if (viewsById?.[id]?.componentType !== 'WarningsTimeline') {
        return;
      }

      const disabledForGroupIds = Object.entries(featureGroupIdsBySourceId)
        .filter(
          ([sourcePanelId]) =>
            !isWarningsTimelineLinkSourceAllowed(
              viewsById?.[sourcePanelId]?.componentType,
            ),
        )
        .flatMap(([, groupIds]) => groupIds);

      // If a source panel is no longer allowed to target warning timeline
      // (e.g. view changed from WarningList to Soundings/TimeSeries),
      // reflect that immediately in view state by unselecting the timeline
      // option for the affected feature groups.
      disabledForGroupIds.forEach((groupId) => {
        const group = draft.features.groups.find(
          ({ id: gid }) => gid === groupId,
        );
        if (!group) {
          return;
        }
        group.selected = group.selected.filter(
          (selectedId) => selectedId !== id,
        );
      });

      if (!draft.features.sourcesById.some((source) => source.id === id)) {
        draft.features.sourcesById.push({
          id,
          name: constructSyncSourceDisplayName(id, viewsById?.[id], id),
          disabled: takenWarningTimelineIds.has(id),
          disabledForGroupIds,
        });
      }
    });
  });
  return initialState;
};

const groups = (store: CoreAppStore): SynchronizationGroups =>
  store.syncGroups!.groups;
const sources = (store: CoreAppStore): SynchronizationSources =>
  store.syncGroups!.sources;
export const getWorkspaceViewsById = (
  store: CoreAppStore,
):
  | Record<string, { id?: string; title?: string; componentType?: string }>
  | undefined => {
  const storeWithWorkspace = store as CoreAppStore & {
    workspace?: {
      views?: {
        byId?: Record<
          string,
          { id?: string; title?: string; componentType?: string }
        >;
      };
    };
  };

  return storeWithWorkspace.workspace?.views?.byId;
};

export const createSyncGroupViewStateSelector = createSelector(
  groups,
  sources,
  getWorkspaceViewsById,
  (syncGroups, syncSources, viewsById) =>
    createSyncGroupViewState(
      syncGroups,
      syncSources,
      viewsById,
      getFeatureLinksForAllPanelsFromState({
        groups: syncGroups,
        sources: syncSources,
      } as never),
    ),
);
